import Notify from "devextreme/ui/notify";
//DBTables
import { ModuleTypeId } from "../../../types/DBTables";
import { UseFormReturn } from "react-hook-form";

import NumberEditor from "../../Global/Editors/NumberEditor";
import { useGlobalData } from "../../GlobalDataProvider";
import { IModuleRound, useRound } from "../../../modules/roundHandler";
import { IGlobalData, MapTooltip } from "../../../types/globalData";
import { logAndNotifyError } from "../../../modules/logger";
import { IModuleByggnadsdata } from "../../../types/ModuleDatas";
import { nameof } from "../../Global/Utils";
import BFLNote from "../BFL/BFLNote";

interface IProps {
  module: IModuleRound;
  data: any;
  formHook: UseFormReturn;
}

const RoundByggnadsData = ({ module, data, formHook }: IProps) => {
  const globalData = useGlobalData();
  const roundHandler = useRound();
  const moduleData = module.data as IModuleByggnadsdata;

  const setÅrligVärmeFörbrukningFromLastYear = () => {
    var lastRound = roundHandler.getLastFinishedRound();
    let lastRoundValue: number | undefined;
    if (lastRound) {
      const byggnadsDataModuleRound = lastRound.data.modules.find((m) => m.moduleTypeId === ModuleTypeId.Byggnadsdata);
      if (byggnadsDataModuleRound?.data) {
        const byggnadsData = byggnadsDataModuleRound.data as IModuleByggnadsdata;
        lastRoundValue = byggnadsData.årligVärmeFörbrukning;
      }
    }

    lastRoundValue = lastRoundValue ? Math.round(lastRoundValue) : undefined;

    if (lastRoundValue) Notify("Värde angivet från senaste rondering", "success");
    else logAndNotifyError("Inget värde för 'årlig värmeförbrukning' hittades");

    formHook.setValue(nameof<IModuleByggnadsdata>("årligVärmeFörbrukning"), lastRoundValue, { shouldValidate: true });
  };

  const setÅrligEnergiFörbrukningFromLastYear = () => {
    var lastRound = roundHandler.getLastFinishedRound();
    let lastRoundValue: number | undefined;
    if (lastRound) {
      const byggnadsDataModuleRound = lastRound.data.modules.find((m) => m.moduleTypeId === ModuleTypeId.Byggnadsdata);
      if (byggnadsDataModuleRound?.data) {
        const byggnadsData = byggnadsDataModuleRound.data as IModuleByggnadsdata;
        lastRoundValue = byggnadsData.årligEnergiFörbrukning;
      }
    }

    lastRoundValue = lastRoundValue ? Math.round(lastRoundValue) : undefined;

    if (lastRoundValue) Notify("Värde angivet från senaste rondering", "success");
    else logAndNotifyError("Inget värde för 'Byggnadens årliga total energi-användning (kWh)' hittades");

    formHook.setValue(nameof<IModuleByggnadsdata>("årligEnergiFörbrukning"), lastRoundValue, { shouldValidate: true });
  };

  const setÅrligVärmeFörbrukningFromSchablon = () => {
    let schablon: undefined | number = undefined;
    //MAX(AN19:BH19*B12)*'Grunddata BIM'!G11/'Grunddata BIM'!$G$29

    const global = globalData.data as IGlobalData;
    const bimHandler = roundHandler.getBimHandler();

    const verksamhet = moduleData.verksamhet as string;
    const nybyggnadsAr = moduleData.nybyggnadsår as string;

    const verksamhetRow = global.Byggnadsdata.SchablonÅrligEl.find((r) => r.Verksamhet === verksamhet && r.Nybyggnadsår === nybyggnadsAr); // MAX(AN19:BH19)
    const byggnadsArea = moduleData.byggnadArea as number; // B12

    if (verksamhetRow && bimHandler.G11_GeografiskJusteringsFaktor && bimHandler.BIM_Vägledande_VerkninsgradAktuellVärmekälla) {
      schablon = (verksamhetRow.Värde * byggnadsArea * bimHandler.G11_GeografiskJusteringsFaktor) / bimHandler.BIM_Vägledande_VerkninsgradAktuellVärmekälla;
    }

    schablon = schablon ? Math.round(schablon) : undefined;

    if (schablon) Notify("Schablonvärde angivet", "success");
    else logAndNotifyError("Inget schablonvärde för 'årlig värmeförbrukning kunde beräknas");

    formHook.setValue(nameof<IModuleByggnadsdata>("årligVärmeFörbrukning"), schablon, { shouldValidate: true });
  };

  const setÅrligEnergiFörbrukningFromSchablon = () => {
    let schablon: undefined | number = undefined;
    //=MAX(AN20:BH20*B12)

    const global = globalData.data as IGlobalData;
    //const bimHandler = roundHandler.getBimHandler();

    const verksamhet = moduleData.verksamhet as string;
    const verksamhetRow = global.Byggnadsdata.Verksamhet.find((r) => r.Verksamhet === verksamhet);

    const byggnadsArea = moduleData.byggnadArea as number; // B12

    if (verksamhetRow) {
      schablon = verksamhetRow.SchablonDriftEl * byggnadsArea;
    }

    schablon = schablon ? Math.round(schablon) : undefined;

    if (schablon) Notify("Schablonvärde angivet", "success");
    else logAndNotifyError("Inget schablonvärde för 'Byggnadens årliga total energi-användning (kWh)'");

    formHook.setValue(nameof<IModuleByggnadsdata>("årligEnergiFörbrukning"), schablon, { shouldValidate: true });
  };

  const värmekälla = moduleData.energikälla;
  const årligVärmeanvändningFråga = globalData.data?.Byggnadsdata.Värmekälla.find((r) => r.Värmekälla === värmekälla)?.ÅrligVärmeanvändningFråga ?? "Byggnadens årliga värmeanvändning (kWh)";
  const årligEnergianvändningFråga = globalData.data?.Byggnadsdata.Värmekälla.find((r) => r.Värmekälla === värmekälla)?.ÅrligEnergianvändningFråga ?? "Byggnadens årliga total energi-användning (kWh)";

  return (
    <>
      <div className="row ml-0">
        <div className="col-lg-12">
          <NumberEditor
            label={årligVärmeanvändningFråga}
            fieldName={nameof<IModuleByggnadsdata>("årligVärmeFörbrukning")}
            tooltipId={nameof<MapTooltip>("Byggnadsdata_årligVärmeFörbrukning")}
            formHook={formHook}
            options={{
              validate: {
                required: (v) => {
                  return !v ? "Måste fyllas i" : undefined;
                },
              },
            }}
          >
            <button className="btn btn-info dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false">
              Mer
            </button>
            <ul className="dropdown-menu">
              <li className="dropdown-item" onClick={setÅrligVärmeFörbrukningFromLastYear}>
                Hämta från senaste rondering
              </li>
              <li className="dropdown-item" onClick={setÅrligVärmeFörbrukningFromSchablon}>
                Hämta schablonvärde
              </li>
            </ul>
          </NumberEditor>
        </div>
      </div>

      <div className="row ml-0">
        <div className="col-lg-12">
          <NumberEditor
            label={årligEnergianvändningFråga}
            fieldName={nameof<IModuleByggnadsdata>("årligEnergiFörbrukning")}
            tooltipId={nameof<MapTooltip>("Byggnadsdata_årligEnergiFörbrukning")}
            formHook={formHook}
            options={{
              validate: {
                required: (v) => {
                  return !v ? "Måste fyllas i" : undefined;
                },
              },
            }}
          >
            <button className="btn btn-info dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false">
              Mer
            </button>
            <ul className="dropdown-menu">
              <li className="dropdown-item" onClick={setÅrligEnergiFörbrukningFromLastYear}>
                Hämta från senaste rondering
              </li>
              <li className="dropdown-item" onClick={setÅrligEnergiFörbrukningFromSchablon}>
                Hämta schablonvärde
              </li>
            </ul>
          </NumberEditor>
        </div>
      </div>
      <BFLNote formHook={formHook} />
    </>
  );
};

export default RoundByggnadsData;
